// src/components/Bank/BankDashboard.js

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Spinner, Badge, Nav, Form, Alert, Modal, Tabs, Tab, ButtonGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { apiClient, bankClientApi, bankingCommandApi } from '../../services/api-client';
import logger from '../../utils/logger';
import { formatCurrency, formatDate } from '../../utils/formatting';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './BankDashboard.css';


// Status badge component
const BankUserStatusBadge = ({ status }) => {
	const getGradient = () => {
		switch (status) {
			case 'active': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
			case 'inactive': return 'linear-gradient(135deg, #a8a8a8 0%, #d3d3d3 100%)';
			case 'suspended': return 'linear-gradient(135deg, #fdbb2d 0%, #22c1c3 100%)';
			default: return 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)';
		}
	};

	return (
		<span
			className="badge-glass"
			style={{
				background: getGradient(),
				color: 'white',
				padding: '0.3rem 0.8rem',
				borderRadius: '8px',
				fontSize: '0.75rem',
				fontWeight: '600',
				border: 'none',
				textTransform: 'capitalize',
				letterSpacing: '0.5px'
			}}
		>
			{status}
		</span>
	);
};

// Account type badge component
const AccountTypeBadge = ({ type }) => {
	const getGradient = () => {
		switch (type?.toLowerCase()) {
			case 'checking': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
			case 'savings': return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
			case 'credit': return 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)';
			case 'investment': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
			case 'loan': return 'linear-gradient(135deg, #fdbb2d 0%, #22c1c3 100%)';
			default: return 'linear-gradient(135deg, #a8a8a8 0%, #d3d3d3 100%)';
		}
	};

	return (
		<span
			className="badge-glass"
			style={{
				background: getGradient(),
				color: 'white',
				padding: '0.3rem 0.8rem',
				borderRadius: '8px',
				fontSize: '0.75rem',
				fontWeight: '600',
				border: 'none',
				textTransform: 'uppercase',
				letterSpacing: '0.5px'
			}}
		>
			{type || 'Other'}
		</span>
	);
};

// Report Section Component
const ReportSection = ({ section }) => {
	if (!section) return null;

	return (
		<div className="glass-card mb-3">
			<div className="glass-header">
				<h5 className="mb-0 text-glass-primary">{section.title}</h5>
			</div>
			<div className="p-4">
				<pre className="report-content mb-0 text-glass-primary" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
					{section.content}
				</pre>

				{section.id === 'accountSummary' && section.metrics && (
					<div className="mt-3 p-3 glass-card">
						<h6 className="text-glass-primary mb-3">Key Metrics:</h6>
						<Row>
							<Col xs={6} md={4} className="mb-2">
								<div className="stats-card">
									<small className="text-glass-muted">Total Balance</small>
									<p className="mb-1 font-weight-bold text-glass-primary">
										{formatCurrency(section.metrics.totalBalance)}
									</p>
								</div>
							</Col>
							<Col xs={6} md={4} className="mb-2">
								<div className="stats-card">
									<small className="text-glass-muted">Income</small>
									<p className="mb-1 font-weight-bold text-success">
										{formatCurrency(section.metrics.income)}
									</p>
								</div>
							</Col>
							<Col xs={6} md={4} className="mb-2">
								<div className="stats-card">
									<small className="text-glass-muted">Expenses</small>
									<p className="mb-1 font-weight-bold text-danger">
										{formatCurrency(section.metrics.expenses)}
									</p>
								</div>
							</Col>
							<Col xs={6} md={4} className="mb-2">
								<div className="stats-card">
									<small className="text-glass-muted">Net Change</small>
									<p className="mb-1 font-weight-bold text-glass-primary">
										{formatCurrency(section.metrics.netChange)}
									</p>
								</div>
							</Col>
							<Col xs={6} md={4} className="mb-2">
								<div className="stats-card">
									<small className="text-glass-muted">Daily Average</small>
									<p className="mb-1 font-weight-bold text-glass-primary">
										{formatCurrency(section.metrics.averageDailySpend)}/day
									</p>
								</div>
							</Col>
						</Row>
					</div>
				)}

				{section.id === 'behaviorPreferences' && section.categories && (
					<div className="mt-3">
						<h6 className="text-glass-primary mb-3">Top Categories:</h6>
						<div className="table-responsive">
							<table className="table table-glass">
								<thead>
									<tr>
										<th className="border-0 bg-transparent text-glass-primary">Category</th>
										<th className="border-0 bg-transparent text-glass-primary">Count</th>
										<th className="border-0 bg-transparent text-glass-primary">Total</th>
										<th className="border-0 bg-transparent text-glass-primary">Percent</th>
									</tr>
								</thead>
								<tbody>
									{section.categories.map((cat, index) => (
										<tr key={index}>
											<td className="border-0 bg-transparent text-glass-primary">{cat.name}</td>
											<td className="border-0 bg-transparent text-glass-primary">{cat.count}</td>
											<td className="border-0 bg-transparent text-glass-primary font-weight-bold">
												{formatCurrency(cat.total)}
											</td>
											<td className="border-0 bg-transparent text-glass-primary">
												{cat.percent.toFixed(1)}%
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

const BankDashboard = () => {
	const navigate = useNavigate();

	// State management
	const [activeTab, setActiveTab] = useState('overview');
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [stats, setStats] = useState(null);
	const [bankUsers, setBankUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [userAccounts, setUserAccounts] = useState([]);
	const [userTransactions, setUserTransactions] = useState([]);
	const [transactionFilters, setTransactionFilters] = useState({
		startDate: '',
		endDate: '',
		accountId: '',
		type: ''
	});
	const [dateRange, setDateRange] = useState('7');
	const [activityData, setActivityData] = useState([]);

	// Consolidated report state (single API approach)
	const [showReportModal, setShowReportModal] = useState(false);
	const [reportLoading, setReportLoading] = useState(false);
	const [reportData, setReportData] = useState(null);
	const [reportError, setReportError] = useState(null);
	const [reportOptions, setReportOptions] = useState({
		timeframe: '30d',
		includeDetailed: true,
	});

	// Bulk report state
	const [showBulkReportModal, setShowBulkReportModal] = useState(false);
	const [bulkReportLoading, setBulkReportLoading] = useState(false);
	const [bulkReportResults, setBulkReportResults] = useState(null);
	const [bulkReportError, setBulkReportError] = useState(null);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [bulkReportOptions, setBulkReportOptions] = useState({
		timeframe: '30d',
		includeDetailed: true
	});

	// Fetch client stats
	const fetchStats = async () => {
		try {
			logger.info('Fetching stats...');
			const response = await apiClient.get('/bank-client/stats');
			logger.info('Stats response:', response.data);

			if (response.data && response.data.success) {
				setStats(response.data.data);
			} else {
				logger.error('Invalid stats response format:', response.data);
				setError('Failed to load dashboard statistics. Invalid response format.');
			}
		} catch (err) {
			logger.error('Failed to fetch bank stats:', err);
			setError('Failed to load dashboard statistics. Please try again later.');
		}
	};

	// Fetch bank users
	const fetchBankUsers = async () => {
		try {
			const response = await apiClient.get('/bank-client/users');
			setBankUsers(response.data.data || []);
		} catch (err) {
			logger.error('Failed to fetch bank users:', err);
			setError('Failed to load bank users. Please try again later.');
		}
	};

	// Fetch user accounts
	const fetchUserAccounts = async (bankUserId) => {
		if (!bankUserId) return;
		try {
			const response = await apiClient.get(`/bank-client/users/${bankUserId}/accounts`);
			setUserAccounts(response.data.data || []);
		} catch (err) {
			logger.error(`Failed to fetch accounts for user ${bankUserId}:`, err);
			setError('Failed to load user accounts. Please try again later.');
		}
	};

	// Fetch user transactions with filters
	const fetchUserTransactions = async (bankUserId) => {
		if (!bankUserId) return;
		try {
			let url = `/bank-client/users/${bankUserId}/transactions?`;

			// Add filters to URL
			if (transactionFilters.startDate) {
				url += `&startDate=${transactionFilters.startDate}`;
			}
			if (transactionFilters.endDate) {
				url += `&endDate=${transactionFilters.endDate}`;
			}
			if (transactionFilters.accountId) {
				url += `&accountId=${transactionFilters.accountId}`;
			}
			if (transactionFilters.type) {
				url += `&type=${transactionFilters.type}`;
			}

			const response = await apiClient.get(url);
			setUserTransactions(response.data.data || []);
		} catch (err) {
			logger.error(`Failed to fetch transactions for user ${bankUserId}:`, err);
			setError('Failed to load user transactions. Please try again later.');
		}
	};

	// Fetch activity data
	const fetchActivityData = async () => {
		try {
			const endDate = new Date();
			const startDate = new Date();
			startDate.setDate(endDate.getDate() - parseInt(dateRange));

			const response = await apiClient.get(`/bank-client/activity?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`);
			setActivityData(response.data.data || []);
		} catch (err) {
			logger.error('Failed to fetch activity data:', err);
			setError('Failed to load activity data. Please try again later.');
		}
	};

	// Optimized single API call for both JSON and PDF generation
	const generateReport = async (format = 'json') => {
		if (!selectedUser) return;

		setReportLoading(true);
		setReportError(null);
		setReportData(null);

		try {
			const token = localStorage.getItem('token');
			if (!token) {
				setReportError('Authentication required. Please log in again.');
				setReportLoading(false);
				return;
			}

			// Single API call that can handle JSON, HTML, and PDF formats
			const response = await fetch('/api/banking-command/report', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				},
				body: JSON.stringify({
					userId: selectedUser.bankUserId,
					timeframe: reportOptions.timeframe,
					includeDetailed: reportOptions.includeDetailed,
					format: format
				})
			});

			if (!response.ok) {
				if (response.status === 401) {
					throw new Error('Authentication expired. Please log in again.');
				} else if (response.status === 500) {
					const errorText = await response.text();
					throw new Error(`Server error: ${errorText}`);
				} else {
					throw new Error(`HTTP ${response.status}: ${response.statusText}`);
				}
			}

			const contentType = response.headers.get('content-type');

			if (format === 'html' && contentType && contentType.includes('text/html')) {
				// Handle HTML response - open in new window AND download
				const htmlContent = await response.text();

				// Open in new window
				const newWindow = window.open('', '_blank');
				if (newWindow) {
					newWindow.document.write(htmlContent);
					newWindow.document.close();
				}

				// Also create and trigger download
				const blob = new Blob([htmlContent], { type: 'text/html' });
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = `banking-report-${selectedUser.bankUserId}-${reportOptions.timeframe}.html`;

				// Append to body, click, and remove
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				// Clean up the object URL
				window.URL.revokeObjectURL(url);

				// Update the alert message
				if (newWindow) {
					alert('HTML report opened in new window and downloaded to your device');
				} else {
					alert('HTML report downloaded (popup blocked - check downloads folder)');
				}
			} else if (format === 'pdf' && contentType && contentType.includes('text/html')) {
				// Handle PDF-ready HTML response
				const htmlContent = await response.text();
				const newWindow = window.open('', '_blank');
				if (newWindow) {
					newWindow.document.write(htmlContent);
					newWindow.document.close();
					// Auto-trigger print dialog after a short delay
					setTimeout(() => {
						newWindow.print();
					}, 500);
					alert('PDF-ready report opened. Print dialog will appear shortly.');
				}
			} else if (format === 'pdf' && contentType && contentType.includes('application/pdf')) {
				// Handle direct PDF response (if implemented later)
				const blob = await response.blob();
				if (blob.size === 0) {
					throw new Error('Received empty PDF file');
				}

				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				const filename = `banking-report-${selectedUser.bankUserId}-${reportOptions.timeframe}.pdf`;
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
				alert(`PDF report "${filename}" downloaded successfully!`);
			} else {
				// Handle JSON response
				const data = await response.json();
				if (data.success && data.data) {
					setReportData(data.data);
					setShowReportModal(true);
				} else {
					setReportError('Failed to generate report. Invalid response format.');
				}
			}

		} catch (err) {
			logger.error(`Failed to generate report for user ${selectedUser.bankUserId}:`, err);
			setReportError(`Failed to generate report: ${err.message}`);
		} finally {
			setReportLoading(false);
		}
	};

	// Generate bulk reports for selected users
	const generateBulkReports = async () => {
		if (selectedUsers.length === 0) {
			setBulkReportError('Please select at least one user');
			return;
		}

		setBulkReportLoading(true);
		setBulkReportError(null);
		setBulkReportResults(null);

		try {
			const response = await bankingCommandApi.generateBulkReports(
				selectedUsers,
				bulkReportOptions
			);

			if (response.success && response.data) {
				setBulkReportResults(response.data);
			} else {
				setBulkReportError('Failed to generate bulk reports. Invalid response format.');
			}
		} catch (err) {
			logger.error('Failed to generate bulk reports:', err);
			setBulkReportError(`Failed to generate bulk reports: ${err.message}`);
		} finally {
			setBulkReportLoading(false);
		}
	};

	// Toggle user selection for bulk reports
	const toggleUserSelection = (userId) => {
		if (selectedUsers.includes(userId)) {
			setSelectedUsers(selectedUsers.filter(id => id !== userId));
		} else {
			setSelectedUsers([...selectedUsers, userId]);
		}
	};

	// Select all users for bulk reports
	const selectAllUsers = () => {
		if (selectedUsers.length === bankUsers.length) {
			setSelectedUsers([]);
		} else {
			setSelectedUsers(bankUsers.map(user => user.bankUserId));
		}
	};

	// Initial data load
	useEffect(() => {
		const loadDashboardData = async () => {
			setLoading(true);
			setError(null);

			try {
				logger.info('Loading dashboard data...');
				await fetchStats();
				await fetchBankUsers();
				await fetchActivityData();
				logger.info('Dashboard data loaded successfully');
			} catch (err) {
				logger.error('Error loading dashboard data:', err);
				setError('Failed to load dashboard data. Please try again later.');
			} finally {
				setLoading(false);
			}
		};

		loadDashboardData();
	}, []);

	// Load user details when selected
	useEffect(() => {
		if (selectedUser) {
			fetchUserAccounts(selectedUser.bankUserId);
			fetchUserTransactions(selectedUser.bankUserId);
		}
	}, [selectedUser]);

	// Reload activity data when date range changes
	useEffect(() => {
		fetchActivityData();
	}, [dateRange]);

	// Handle user selection
	const handleUserSelect = (user) => {
		setSelectedUser(user);
		setReportData(null);
		setActiveTab('userDetails');
	};

	// Apply transaction filters
	const handleApplyFilters = (e) => {
		e.preventDefault();
		if (selectedUser) {
			fetchUserTransactions(selectedUser.bankUserId);
		}
	};

	// Reset transaction filters
	const handleResetFilters = () => {
		setTransactionFilters({
			startDate: '',
			endDate: '',
			accountId: '',
			type: ''
		});

		if (selectedUser) {
			fetchUserTransactions(selectedUser.bankUserId);
		}
	};

	// Report Modal with optimized generation buttons
	const renderReportModal = () => (
		<Modal show={showReportModal} onHide={() => setShowReportModal(false)} size="lg" backdrop="static" className="modal-glass">
			<Modal.Header closeButton className="glass-header">
				<Modal.Title className="text-glass-primary">Banking Intelligence Report</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{reportError && (
					<div className="alert-glass alert-glass-danger p-3 mb-3">
						{reportError}
					</div>
				)}

				{reportLoading ? (
					<div className="text-center p-4">
						<div className="spinner-glass mx-auto mb-3" style={{ width: '40px', height: '40px' }}></div>
						<p className="text-glass-primary">Generating report...</p>
					</div>
				) : reportData ? (
					<div className="report-container">
						<h3 className="mb-3 text-glass-primary">{reportData.title}</h3>
						<p className="text-glass-primary"><strong>Period:</strong> {reportData.period}</p>
						<p className="text-glass-primary"><strong>Generated:</strong> {formatDate(reportData.generated)}</p>

						{/* Report format buttons */}
						<div className="glass-card mb-4 p-3">
							<h6 className="mb-3 text-glass-primary">View Report in Different Formats:</h6>
							<div className="d-flex gap-2 flex-wrap">
								<button
									className="btn btn-glass-success"
									onClick={() => generateReport('html')}
									disabled={reportLoading || !selectedUser}
								>
									📄 Open HTML Report
								</button>
								<button
									className="btn btn-glass-danger"
									onClick={() => generateReport('pdf')}
									disabled={reportLoading || !selectedUser}
								>
									📁 Print/Save as PDF
								</button>
							</div>
							<small className="text-glass-muted d-block mt-2">
								HTML report opens in a new window with interactive features.
								PDF option opens print-ready version.
							</small>
						</div>

						<div className="nav-tabs-glass">
							<ul className="nav nav-tabs border-0">
								<li className="nav-item">
									<button className="nav-link border-0 active">Report Sections</button>
								</li>
								<li className="nav-item">
									<button className="nav-link border-0">Summary</button>
								</li>
								<li className="nav-item">
									<button className="nav-link border-0">Raw JSON</button>
								</li>
							</ul>
						</div>

						{reportData.sections.map((section) => (
							<ReportSection key={section.id} section={section} />
						))}
					</div>
				) : (
					<div className="alert-glass alert-glass-info p-4">
						<h6 className="text-glass-primary">Banking Intelligence Report Generator</h6>
						<p className="text-glass-primary">Generate comprehensive financial intelligence reports with AI-powered insights.</p>

						<div className="mt-3">
							<h6 className="text-glass-primary">Report Options:</h6>
							<Form>
								<Form.Group className="mb-3">
									<Form.Label className="text-glass-primary font-weight-bold">Time Period</Form.Label>
									<Form.Select
										className="form-select-glass"
										value={reportOptions.timeframe}
										onChange={(e) => setReportOptions({ ...reportOptions, timeframe: e.target.value })}
									>
										<option value="30d">Last 30 Days</option>
										<option value="90d">Last 90 Days</option>
										<option value="180d">Last 180 Days</option>
										<option value="1y">Last Year</option>
									</Form.Select>
								</Form.Group>
								<Form.Group className="mb-3">
									<Form.Check
										type="checkbox"
										label="Include Detailed Analysis Sections"
										className="text-glass-primary"
										checked={reportOptions.includeDetailed}
										onChange={(e) => setReportOptions({ ...reportOptions, includeDetailed: e.target.checked })}
									/>
								</Form.Group>
							</Form>
						</div>

						<div className="d-grid gap-2 d-md-flex justify-content-md-start mt-4">
							<button
								className="btn btn-glass-primary me-2"
								onClick={() => generateReport('json')}
								disabled={reportLoading || !selectedUser}
							>
								{reportLoading ? 'Generating...' : '📊 Generate JSON Report'}
							</button>
							<button
								className="btn btn-glass-success me-2"
								onClick={() => generateReport('html')}
								disabled={reportLoading || !selectedUser}
							>
								📄 Generate HTML Report
							</button>
							<button
								className="btn btn-glass-danger"
								onClick={() => generateReport('pdf')}
								disabled={reportLoading || !selectedUser}
							>
								🖨️ Generate PDF Report
							</button>
						</div>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer>
				{!reportLoading && (
					<>
						{reportData && (
							<div className="me-auto">
								<small className="text-glass-muted">
									Generated in {reportData._metadata?.generationTime || 'unknown'}ms
									{reportData._metadata?.fromCache && ' (from cache)'}
								</small>
							</div>
						)}
						<button className="btn btn-glass" onClick={() => setShowReportModal(false)}>
							Close
						</button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);

	// Bulk Report Modal
	const renderBulkReportModal = () => (
		<Modal show={showBulkReportModal} onHide={() => setShowBulkReportModal(false)} size="lg" backdrop="static" className="modal-glass">
			<Modal.Header closeButton className="glass-header">
				<Modal.Title className="text-glass-primary">Bulk Banking Intelligence Reports</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{bulkReportError && (
					<div className="alert-glass alert-glass-danger p-3 mb-3">
						{bulkReportError}
					</div>
				)}

				{bulkReportLoading ? (
					<div className="text-center p-4">
						<div className="spinner-glass mx-auto mb-3" style={{ width: '40px', height: '40px' }}></div>
						<p className="text-glass-primary">Generating reports for {selectedUsers.length} users...</p>
					</div>
				) : bulkReportResults ? (
					<div>
						<div className={`alert-glass p-3 mb-3 ${bulkReportResults.successful === bulkReportResults.total ? 'alert-glass-success' : 'alert-glass-warning'}`}>
							Generated {bulkReportResults.successful} of {bulkReportResults.total} reports successfully.
							{bulkReportResults.failed > 0 && ` ${bulkReportResults.failed} reports failed to generate.`}
						</div>

						<h5 className="text-glass-primary">Results</h5>
						<div className="table-responsive">
							<table className="table table-glass">
								<thead>
									<tr>
										<th className="border-0 bg-transparent text-glass-primary">Bank User ID</th>
										<th className="border-0 bg-transparent text-glass-primary">Status</th>
										<th className="border-0 bg-transparent text-glass-primary">Details</th>
									</tr>
								</thead>
								<tbody>
									{bulkReportResults.results.map((result, index) => {
										const user = bankUsers.find(u => u.bankUserId === result.userId);
										return (
											<tr key={index}>
												<td className="border-0 bg-transparent text-glass-primary">{result.userId}</td>
												<td className="border-0 bg-transparent">
													{result.status === 'success' ? (
														<BankUserStatusBadge status="active" />
													) : (
														<BankUserStatusBadge status="suspended" />
													)}
												</td>
												<td className="border-0 bg-transparent">
													{result.status === 'success' ? (
														<button
															className="btn btn-glass btn-sm"
															onClick={() => {
																setSelectedUser(user);
																setReportData(result.data);
																setShowBulkReportModal(false);
																setShowReportModal(true);
															}}
														>
															View Report
														</button>
													) : (
														<span className="text-danger">{result.error}</span>
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				) : (
					<>
						<h5 className="text-glass-primary">Report Options</h5>
						<Form>
							<Form.Group className="mb-3">
								<Form.Label className="text-glass-primary font-weight-bold">Time Period</Form.Label>
								<Form.Select
									className="form-select-glass"
									value={bulkReportOptions.timeframe}
									onChange={(e) => setBulkReportOptions({ ...bulkReportOptions, timeframe: e.target.value })}
								>
									<option value="30d">Last 30 Days</option>
									<option value="90d">Last 90 Days</option>
									<option value="180d">Last 180 Days</option>
									<option value="1y">Last Year</option>
								</Form.Select>
							</Form.Group>
							<Form.Group className="mb-3">
								<Form.Check
									type="checkbox"
									label="Include Detailed Sections"
									className="text-glass-primary"
									checked={bulkReportOptions.includeDetailed}
									onChange={(e) => setBulkReportOptions({ ...bulkReportOptions, includeDetailed: e.target.checked })}
								/>
							</Form.Group>
						</Form>

						<h5 className="text-glass-primary mt-4">Select Users</h5>
						<div className="d-flex justify-content-between mb-3">
							<button
								className="btn btn-glass btn-sm"
								onClick={selectAllUsers}
							>
								{selectedUsers.length === bankUsers.length ? 'Deselect All' : 'Select All'}
							</button>
							<span className="text-glass-primary">{selectedUsers.length} of {bankUsers.length} users selected</span>
						</div>

						<div className="glass-scrollbar" style={{ maxHeight: '300px', overflowY: 'auto' }}>
							<table className="table table-glass">
								<thead>
									<tr>
										<th className="border-0 bg-transparent text-glass-primary">Select</th>
										<th className="border-0 bg-transparent text-glass-primary">Bank User ID</th>
										<th className="border-0 bg-transparent text-glass-primary">Name</th>
										<th className="border-0 bg-transparent text-glass-primary">Status</th>
										<th className="border-0 bg-transparent text-glass-primary">Accounts</th>
									</tr>
								</thead>
								<tbody>
									{bankUsers.map((user, index) => (
										<tr key={index}>
											<td className="border-0 bg-transparent">
												<Form.Check
													type="checkbox"
													checked={selectedUsers.includes(user.bankUserId)}
													onChange={() => toggleUserSelection(user.bankUserId)}
													id={`user-check-${index}`}
												/>
											</td>
											<td className="border-0 bg-transparent text-glass-primary text-truncate" style={{ maxWidth: '150px' }}>
												{user.bankUserId}
											</td>
											<td className="border-0 bg-transparent text-glass-primary">{user.name || 'N/A'}</td>
											<td className="border-0 bg-transparent">
												<BankUserStatusBadge status={user.status} />
											</td>
											<td className="border-0 bg-transparent text-glass-primary">{user.accountCount || 0}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{!bulkReportLoading && (
					bulkReportResults ? (
						<>
							<button className="btn btn-glass-primary" onClick={() => {
								setBulkReportResults(null);
								setSelectedUsers([]);
							}}>
								Generate More Reports
							</button>
							<button className="btn btn-glass" onClick={() => setShowBulkReportModal(false)}>
								Close
							</button>
						</>
					) : (
						<>
							<button
								className="btn btn-glass-primary"
								onClick={generateBulkReports}
								disabled={bulkReportLoading || selectedUsers.length === 0}
							>
								Generate Bulk Reports
							</button>
							<button className="btn btn-glass" onClick={() => setShowBulkReportModal(false)}>
								Cancel
							</button>
						</>
					)
				)}
			</Modal.Footer>
		</Modal>
	);

	// Render dashboard overview
	const renderOverview = () => (
		<>
			<Row className="mt-4">
				<Col md={12} lg={8}>
					<div className="glass-card mb-4">
						<div className="glass-header">
							<h5 className="mb-0 text-glass-primary">Client Overview</h5>
						</div>
						<div className="p-4">
							{loading ? (
								<div className="text-center p-4">
									<div className="spinner-glass mx-auto mb-3" style={{ width: '40px', height: '40px' }}></div>
									<p className="text-glass-primary">Loading statistics...</p>
								</div>
							) : stats ? (
								<Row>
									<Col sm={4} className="mb-3">
										<div className="stats-card">
											<h2 style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
												{stats?.users?.total || 0}
											</h2>
											<p className="text-glass-muted">Total Bank Users</p>
										</div>
									</Col>
									<Col sm={4} className="mb-3">
										<div className="stats-card">
											<h2 style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
												{stats?.accounts?.total || 0}
											</h2>
											<p className="text-glass-muted">Active Accounts</p>
										</div>
									</Col>
									<Col sm={4} className="mb-3">
										<div className="stats-card">
											<h2 style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
												{stats?.transactions?.total || 0}
											</h2>
											<p className="text-glass-muted">Total Transactions</p>
										</div>
									</Col>
								</Row>
							) : (
								<div className="alert-glass alert-glass-warning p-3">
									No statistics available
								</div>
							)}
						</div>
					</div>
				</Col>

				<Col md={12} lg={4}>
					<div className="glass-card mb-4">
						<div className="glass-header">
							<h5 className="mb-0 text-glass-primary">Recent Activity</h5>
						</div>
						<div className="p-4">
							<Form.Group className="mb-3">
								<Form.Label className="text-glass-primary font-weight-bold">Time Period</Form.Label>
								<Form.Select
									className="form-select-glass"
									value={dateRange}
									onChange={(e) => setDateRange(e.target.value)}
								>
									<option value="7">Last 7 Days</option>
									<option value="30">Last 30 Days</option>
									<option value="90">Last 90 Days</option>
								</Form.Select>
							</Form.Group>

							{loading ? (
								<div className="text-center">
									<div className="spinner-glass" style={{ width: '30px', height: '30px' }}></div>
								</div>
							) : (
								<div className="chart-container-glass">
									<ResponsiveContainer width="100%" height={200}>
										<BarChart data={activityData}>
											<CartesianGrid strokeDasharray="3 3" opacity={0.3} />
											<XAxis dataKey="date" tick={{ fill: 'rgba(255, 255, 255, 0.8)' }} />
											<YAxis tick={{ fill: 'rgba(255, 255, 255, 0.8)' }} />
											<Tooltip
												contentStyle={{
													background: 'rgba(255, 255, 255, 0.95)',
													backdropFilter: 'blur(10px)',
													border: '1px solid rgba(255, 255, 255, 0.3)',
													borderRadius: '8px',
													color: '#2d3748'
												}}
											/>
											<Legend />
											<Bar dataKey="transactions" fill="url(#gradient1)" name="Transactions" />
											<Bar dataKey="users" fill="url(#gradient2)" name="Active Users" />
											<defs>
												<linearGradient id="gradient1" x1="0" y1="0" x2="0" y2="1">
													<stop offset="5%" stopColor="#667eea" stopOpacity={0.8} />
													<stop offset="95%" stopColor="#764ba2" stopOpacity={0.8} />
												</linearGradient>
												<linearGradient id="gradient2" x1="0" y1="0" x2="0" y2="1">
													<stop offset="5%" stopColor="#4facfe" stopOpacity={0.8} />
													<stop offset="95%" stopColor="#00f2fe" stopOpacity={0.8} />
												</linearGradient>
											</defs>
										</BarChart>
									</ResponsiveContainer>
								</div>
							)}
						</div>
					</div>
				</Col>
			</Row>

			<div className="glass-card mb-4">
				<div className="glass-header d-flex justify-content-between align-items-center">
					<h5 className="mb-0 text-glass-primary">Bank Users</h5>
					<button
						className="btn btn-glass-primary"
						onClick={() => setShowBulkReportModal(true)}
					>
						Generate Bulk Reports
					</button>
				</div>
				<div className="p-4">
					{loading ? (
						<div className="text-center p-4">
							<div className="spinner-glass mx-auto mb-3" style={{ width: '40px', height: '40px' }}></div>
							<p className="text-glass-primary">Loading bank users...</p>
						</div>
					) : bankUsers.length > 0 ? (
						<div className="table-responsive">
							<table className="table table-glass">
								<thead>
									<tr>
										<th className="border-0 bg-transparent text-glass-primary">User ID</th>
										<th className="border-0 bg-transparent text-glass-primary">Name</th>
										<th className="border-0 bg-transparent text-glass-primary">Email</th>
										<th className="border-0 bg-transparent text-glass-primary">Status</th>
										<th className="border-0 bg-transparent text-glass-primary">Created</th>
										<th className="border-0 bg-transparent text-glass-primary">Accounts</th>
										<th className="border-0 bg-transparent text-glass-primary">Actions</th>
									</tr>
								</thead>
								<tbody>
									{bankUsers.map(user => (
										<tr key={user.id}>
											<td className="border-0 bg-transparent text-glass-primary text-truncate" style={{ maxWidth: '150px' }}>
												{user.bankUserId}
											</td>
											<td className="border-0 bg-transparent text-glass-primary font-weight-bold">
												{user.name || 'N/A'}
											</td>
											<td className="border-0 bg-transparent text-glass-primary">{user.email || 'N/A'}</td>
											<td className="border-0 bg-transparent">
												<BankUserStatusBadge status={user.status} />
											</td>
											<td className="border-0 bg-transparent text-glass-primary">
												{formatDate(user.createdAt)}
											</td>
											<td className="border-0 bg-transparent text-glass-primary font-weight-bold">
												{user.accountCount || 0}
											</td>
											<td className="border-0 bg-transparent">
												<button
													className="btn btn-glass-primary btn-sm me-2"
													onClick={() => handleUserSelect(user)}
												>
													View Details
												</button>
												<button
													className="btn btn-glass btn-sm"
													onClick={() => {
														setSelectedUser(user);
														setShowReportModal(true);
													}}
												>
													Generate Report
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<div className="text-center p-4">
							<div className="alert-glass alert-glass-info p-3">
								No bank users found. Add bank users to get started.
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	);

	// Render user details (simplified for brevity - same as original but with glass styling)
	const renderUserDetails = () => {
		if (!selectedUser) {
			return (
				<div className="alert-glass alert-glass-warning p-4">
					No user selected. Please select a user from the overview page.
				</div>
			);
		}

		return (
			<>
				<div className="glass-card mb-4">
					<div className="glass-header d-flex justify-content-between align-items-center">
						<div>
							<h5 className="mb-0 text-glass-primary">User Profile: {selectedUser.name || selectedUser.bankUserId}</h5>
							<small className="text-glass-muted">{selectedUser.email || 'No email'}</small>
						</div>
						<div>
							<div className="btn-group">
								<button
									className="btn btn-glass-primary btn-sm me-2"
									onClick={() => setShowReportModal(true)}
								>
									📊 Generate Report
								</button>
								<button
									className="btn btn-glass-success btn-sm me-2"
									onClick={() => generateReport('html')}
									disabled={reportLoading}
								>
									📄 HTML Report
								</button>
								<button
									className="btn btn-glass-danger btn-sm"
									onClick={() => generateReport('pdf')}
									disabled={reportLoading}
								>
									🖨️ PDF Report
								</button>
							</div>
						</div>
					</div>
					<div className="p-4">
						<Row>
							<Col md={6}>
								<p className="text-glass-primary"><strong>Bank User ID:</strong> {selectedUser.bankUserId}</p>
								<p className="text-glass-primary">
									<strong>Status:</strong> <BankUserStatusBadge status={selectedUser.status} />
								</p>
								<p className="text-glass-primary"><strong>Created:</strong> {formatDate(selectedUser.createdAt)}</p>
							</Col>
							<Col md={6}>
								{selectedUser.metadata && (
									<>
										<p className="text-glass-primary"><strong>Metadata:</strong></p>
										<pre className="glass-card p-2 text-glass-primary" style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '0.8rem' }}>
											{JSON.stringify(selectedUser.metadata, null, 2)}
										</pre>
									</>
								)}
							</Col>
						</Row>
					</div>
				</div>

				<div className="glass-card mb-4">
					<div className="glass-header">
						<h5 className="mb-0 text-glass-primary">Accounts</h5>
					</div>
					<div className="p-4">
						{loading ? (
							<div className="text-center p-4">
								<div className="spinner-glass mx-auto mb-3" style={{ width: '40px', height: '40px' }}></div>
								<p className="text-glass-primary">Loading accounts...</p>
							</div>
						) : userAccounts.length > 0 ? (
							<div className="table-responsive">
								<table className="table table-glass">
									<thead>
										<tr>
											<th className="border-0 bg-transparent text-glass-primary">Account ID</th>
											<th className="border-0 bg-transparent text-glass-primary">Name</th>
											<th className="border-0 bg-transparent text-glass-primary">Type</th>
											<th className="border-0 bg-transparent text-glass-primary">Balance</th>
											<th className="border-0 bg-transparent text-glass-primary">Available</th>
											<th className="border-0 bg-transparent text-glass-primary">Currency</th>
											<th className="border-0 bg-transparent text-glass-primary">Last Updated</th>
										</tr>
									</thead>
									<tbody>
										{userAccounts.map(account => (
											<tr key={account.id}>
												<td className="border-0 bg-transparent text-glass-primary text-truncate" style={{ maxWidth: '150px' }}>
													{account.accountId}
												</td>
												<td className="border-0 bg-transparent text-glass-primary font-weight-bold">
													{account.name}
												</td>
												<td className="border-0 bg-transparent">
													<AccountTypeBadge type={account.type} />
													{account.subtype && <small className="ms-2 text-glass-muted">({account.subtype})</small>}
												</td>
												<td className="border-0 bg-transparent text-glass-primary font-weight-bold">
													{formatCurrency(account.balance, account.currency)}
												</td>
												<td className="border-0 bg-transparent text-glass-primary">
													{formatCurrency(account.availableBalance, account.currency)}
												</td>
												<td className="border-0 bg-transparent text-glass-primary">{account.currency}</td>
												<td className="border-0 bg-transparent text-glass-primary">
													{formatDate(account.lastUpdated)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : (
							<div className="alert-glass alert-glass-info p-3">
								No accounts found for this user.
							</div>
						)}
					</div>
				</div>

				<div className="glass-card mb-4">
					<div className="glass-header">
						<div className="d-flex justify-content-between align-items-center">
							<h5 className="mb-0 text-glass-primary">Transactions</h5>
							<button
								className="btn btn-glass btn-sm"
								type="button"
								onClick={() => {
									const collapseElement = document.getElementById('filterCollapse');
									if (collapseElement.classList.contains('show')) {
										collapseElement.classList.remove('show');
									} else {
										collapseElement.classList.add('show');
									}
								}}
							>
								Filters
							</button>
						</div>
					</div>

					<div className="collapse" id="filterCollapse">
						<div className="p-4 glass-card" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
							<form onSubmit={handleApplyFilters}>
								<div className="row">
									<div className="col-md-3">
										<div className="form-group mb-3">
											<label className="form-label text-glass-primary font-weight-bold">Start Date</label>
											<input
												type="date"
												className="form-control form-control-glass"
												value={transactionFilters.startDate}
												onChange={(e) => setTransactionFilters({ ...transactionFilters, startDate: e.target.value })}
											/>
										</div>
									</div>
									<div className="col-md-3">
										<div className="form-group mb-3">
											<label className="form-label text-glass-primary font-weight-bold">End Date</label>
											<input
												type="date"
												className="form-control form-control-glass"
												value={transactionFilters.endDate}
												onChange={(e) => setTransactionFilters({ ...transactionFilters, endDate: e.target.value })}
											/>
										</div>
									</div>
									<div className="col-md-3">
										<div className="form-group mb-3">
											<label className="form-label text-glass-primary font-weight-bold">Account</label>
											<select
												className="form-select form-select-glass"
												value={transactionFilters.accountId}
												onChange={(e) => setTransactionFilters({ ...transactionFilters, accountId: e.target.value })}
											>
												<option value="">All Accounts</option>
												{userAccounts.map(account => (
													<option key={account.accountId} value={account.accountId}>
														{account.name}
													</option>
												))}
											</select>
										</div>
									</div>
									<div className="col-md-3">
										<div className="form-group mb-3">
											<label className="form-label text-glass-primary font-weight-bold">Type</label>
											<select
												className="form-select form-select-glass"
												value={transactionFilters.type}
												onChange={(e) => setTransactionFilters({ ...transactionFilters, type: e.target.value })}
											>
												<option value="">All Types</option>
												<option value="income">Income</option>
												<option value="expense">Expense</option>
												<option value="transfer">Transfer</option>
											</select>
										</div>
									</div>
								</div>
								<div className="d-flex gap-2">
									<button type="submit" className="btn btn-glass-primary btn-sm">Apply Filters</button>
									<button type="button" className="btn btn-glass btn-sm" onClick={handleResetFilters}>Reset</button>
								</div>
							</form>
						</div>
					</div>

					<div className="p-4">
						{loading ? (
							<div className="text-center p-4">
								<div className="spinner-glass mx-auto mb-3" style={{ width: '40px', height: '40px' }}></div>
								<p className="text-glass-primary">Loading transactions...</p>
							</div>
						) : userTransactions.length > 0 ? (
							<div className="table-responsive">
								<table className="table table-glass">
									<thead>
										<tr>
											<th className="border-0 bg-transparent text-glass-primary">Date</th>
											<th className="border-0 bg-transparent text-glass-primary">Description</th>
											<th className="border-0 bg-transparent text-glass-primary">Amount</th>
											<th className="border-0 bg-transparent text-glass-primary">Account</th>
											<th className="border-0 bg-transparent text-glass-primary">Category</th>
											<th className="border-0 bg-transparent text-glass-primary">Merchant</th>
											<th className="border-0 bg-transparent text-glass-primary">Status</th>
										</tr>
									</thead>
									<tbody>
										{userTransactions.map(transaction => {
											const account = userAccounts.find(a => a.accountId === transaction.accountId);
											return (
												<tr key={transaction.id}>
													<td className="border-0 bg-transparent text-glass-primary">
														{formatDate(transaction.date)}
													</td>
													<td className="border-0 bg-transparent text-glass-primary text-truncate" style={{ maxWidth: '200px' }}>
														{transaction.description}
													</td>
													<td className={`border-0 bg-transparent font-weight-bold ${transaction.amount >= 0 ? 'text-success' : 'text-danger'}`}>
														{formatCurrency(transaction.amount, account?.currency || 'USD')}
													</td>
													<td className="border-0 bg-transparent text-glass-primary">
														{account?.name || transaction.accountId}
													</td>
													<td className="border-0 bg-transparent text-glass-primary">
														{transaction.category || 'Uncategorized'}
													</td>
													<td className="border-0 bg-transparent text-glass-primary">
														{transaction.merchantName || 'N/A'}
													</td>
													<td className="border-0 bg-transparent">
														{transaction.pending ? (
															<span className="badge-glass" style={{ background: 'linear-gradient(135deg, #fdbb2d 0%, #22c1c3 100%)', color: 'white', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.7rem' }}>
																Pending
															</span>
														) : (
															<span className="badge-glass" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.7rem' }}>
																Cleared
															</span>
														)}
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						) : (
							<div className="alert-glass alert-glass-info p-3">
								No transactions found for this user.
							</div>
						)}
					</div>
				</div>
			</>
		);
	};

	return (
		<div className="container-fluid py-4" style={{ minHeight: '100vh' }}>
			<div className="glass-card p-4 mb-4">
				<h1 className="mb-0 text-glass-light">Bank Dashboard</h1>
			</div>

			{error && (
				<div className="alert-glass alert-glass-danger p-3 mb-4" style={{ borderRadius: '16px' }}>
					<button type="button" className="btn-close float-end" onClick={() => setError(null)}></button>
					{error}
				</div>
			)}

			<div className="nav-tabs-glass mb-4">
				<ul className="nav nav-tabs border-0">
					<li className="nav-item">
						<button
							className={`nav-link border-0 ${activeTab === 'overview' ? 'active' : ''}`}
							onClick={() => setActiveTab('overview')}
						>
							Overview
						</button>
					</li>
					<li className="nav-item">
						<button
							className={`nav-link border-0 ${activeTab === 'userDetails' ? 'active' : ''}`}
							onClick={() => activeTab !== 'userDetails' && selectedUser && setActiveTab('userDetails')}
							disabled={!selectedUser}
						>
							User Details
						</button>
					</li>
				</ul>
			</div>

			{activeTab === 'overview' && renderOverview()}
			{activeTab === 'userDetails' && renderUserDetails()}

			{/* Report Modals */}
			{renderReportModal()}
			{renderBulkReportModal()}
		</div>
	);
};

export default BankDashboard;